import { createLogger } from "@/logger";

const log = createLogger("reset");

import { db, indexedDbName } from "@/db/db.ts";
import { tables } from "@/db/schema.ts";
import { BootstrapEventsRegistry, IDB_DB_NAME } from "@/models/BootstrapEventsRegistry";
import { databaseMetricsStorageService } from "@/services/DatabaseMetricsStorageService.ts";

export class DatabaseResetService {
  public async softReboot(): Promise<void> {
    try {
      log.warn(`[${this.constructor.name}] Performing SOFT reboot: reloading page`);
    } finally {
      window.location.reload();
    }
  }

  public async hardReboot(): Promise<void> {
    try {
      log.warn(`[${this.constructor.name}] Performing HARD reboot: clearing IndexedDB and metrics storage`);
      await this.clearIndexedDb();
      databaseMetricsStorageService.clearAll();
      const registry = BootstrapEventsRegistry.getInstance();
      await registry.clearAll();
    } finally {
      window.location.reload();
    }
  }

  private async clearIndexedDb(): Promise<void> {
    await new Promise<void>((resolve) => {
      const request = window.indexedDB.deleteDatabase(indexedDbName);
      request.onsuccess = () => {
        log.info(`[${this.constructor.name}] IndexedDB deleted successfully`, { indexedDbName });
        resolve();
      };
      request.onerror = () => {
        log.error(`[${this.constructor.name}] Error deleting IndexedDB`, { indexedDbName });
        resolve();
      };
      request.onblocked = () => {
        log.warn(`[${this.constructor.name}] IndexedDB delete blocked`, { indexedDbName });
        resolve();
      };
    });
  }
}

const truncateTable = async (table: string) => {
  try {
    const sql = `truncate table ${table} cascade;`;
    return await db.$client.query(sql, []);
  } catch (e) {
    return Promise.reject(e);
  }
};

const truncateAllTables = async (tables: string[]): Promise<boolean> => {
  let truncatedTables = [];
  if (tables?.length > 0) {
    for (const table of tables) {
      try {
        await truncateTable(table);
        truncatedTables.push(table);
      } catch (e) {
        console.error({ e });
      }
    }
    if (truncatedTables?.length > 0 && truncatedTables?.length === tables?.length) {
      return true;
    } else {
      return Promise.reject({ error: `Only ${truncatedTables?.length} of ${tables?.length} Tables Truncated.` });
    }
  } else {
    log.debug("No Tables Selected");
    return false;
  }
};

async function renameIndexedDb(oldName: string, newName: string): Promise<boolean> {
  return new Promise((resolve, reject) => {
    // Open the old database
    const oldRequest = indexedDB.open(oldName);

    oldRequest.onsuccess = () => {
      const oldDb = oldRequest.result;
      const objectStoreNames = Array.from(oldDb.objectStoreNames);
      const version = oldDb.version;

      // Open new database with same version
      const newRequest = indexedDB.open(newName, version);

      newRequest.onupgradeneeded = (event) => {
        const newDb = (event.target as IDBOpenDBRequest).result;

        // Copy object stores structure
        objectStoreNames.forEach((storeName) => {
          const oldStore = oldDb.transaction(storeName, "readonly").objectStore(storeName);
          const newStore = newDb.createObjectStore(storeName, {
            keyPath: oldStore.keyPath,
            autoIncrement: oldStore.autoIncrement,
          });

          // Copy indexes
          Array.from(oldStore.indexNames).forEach((indexName) => {
            const index = oldStore.index(indexName);
            newStore.createIndex(indexName, index.keyPath, {
              unique: index.unique,
              multiEntry: index.multiEntry,
            });
          });
        });
      };

      newRequest.onsuccess = async () => {
        const newDb = newRequest.result;

        // Copy data from each object store
        try {
          for (const storeName of objectStoreNames) {
            const oldTransaction = oldDb.transaction(storeName, "readonly");
            const oldStore = oldTransaction.objectStore(storeName);
            const getAllRequest = oldStore.getAll();

            await new Promise<void>((resolveStore, rejectStore) => {
              getAllRequest.onsuccess = () => {
                try {
                  const data = getAllRequest.result;
                  const newTransaction = newDb.transaction(storeName, "readwrite");
                  const newStore = newTransaction.objectStore(storeName);

                  data.forEach((item) => newStore.add(item));

                  newTransaction.oncomplete = () => resolveStore();
                  newTransaction.onerror = () => rejectStore(newTransaction.error);
                  newTransaction.onabort = () => rejectStore(newTransaction.error);
                } catch (e) {
                  rejectStore(e);
                }
              };
              getAllRequest.onerror = () => rejectStore(getAllRequest.error);
            });
          }
        } catch (e) {
          oldDb.close();
          newDb.close();
          reject(false);
          return;
        }

        oldDb.close();
        newDb.close();

        // Delete old database
        const deleteRequest = indexedDB.deleteDatabase(oldName);
        deleteRequest.onsuccess = () => resolve(true);
        deleteRequest.onerror = () => reject(false);
        deleteRequest.onblocked = () => {
          log.debug("DB Blocked");
          reject(false);
        };
      };

      newRequest.onerror = () => {
        oldDb.close();
        reject(false);
      };
    };

    oldRequest.onerror = () => reject(false);
  });
}

const removeBootstrapRegistry = async (name: string): Promise<boolean> => {
  try {
    return await renameIndexedDb(name, `--remove-db---${Date.now()}`);
  } catch (e) {
    return false;
  }
};

export const truncateDatabase = async () => {
  let truncateDB: boolean, removeRegistry: boolean;
  try {
    truncateDB = await truncateAllTables(tables);
  } catch (e) {
    console.error({ e });
    truncateDB = false;
  }

  try {
    removeRegistry = await removeBootstrapRegistry(IDB_DB_NAME);
  } catch (e) {
    console.error("removeBootstrapRegistry", { e });
    removeRegistry = false;
  }

  if (truncateDB && removeRegistry) {
    return true;
  } else {
    return Promise.reject(false);
  }
};

window["truncateDatabase"] = truncateDatabase;

export const databaseResetService = new DatabaseResetService();
